import { useState, useRef, useEffect } from 'react';
import { getImageUrl } from '../../utils/api';

const SearchableSelect = ({ 
    options = [], 
    value, 
    onChange, 
    placeholder = "Ara ve seçin...", 
    showImages = false,
    imageKey = 'image',
    labelKey = 'name',
    valueKey = 'id',
    className = "",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter options based on search term
    const filteredOptions = options.filter(option => {
        const label = option[labelKey] || '';
        return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Get selected option
    const selectedOption = options.find(opt => String(opt[valueKey]) === String(value));

    const handleSelect = (option) => {
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Selected value display */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full px-3 py-2 text-left border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex items-center justify-between ${
                    disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'
                }`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedOption ? (
                        <>
                            {showImages && selectedOption[imageKey] && (
                                <img 
                                    src={getImageUrl(selectedOption[imageKey])} 
                                    alt={selectedOption[labelKey]}
                                    className="w-8 h-8 object-cover rounded"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                            <span className="truncate">{selectedOption[labelKey]}</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Ara..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                        />
                    </div>

                    {/* Options list */}
                    <div className="overflow-y-auto max-h-48">
                        {filteredOptions.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                Sonuç bulunamadı
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = String(option[valueKey]) === String(value);
                                return (
                                    <button
                                        key={option[valueKey]}
                                        type="button"
                                        onClick={() => handleSelect(option)}
                                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors ${
                                            isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                                        }`}
                                    >
                                        {showImages && option[imageKey] && option[valueKey] !== '' && (
                                            <div className="w-10 h-10 flex-shrink-0">
                                                <img 
                                                    src={getImageUrl(option[imageKey])} 
                                                    alt={option[labelKey]}
                                                    className="w-full h-full object-cover rounded"
                                                    onError={(e) => { 
                                                        e.target.style.display = 'none';
                                                        // Placeholder göster
                                                        const placeholder = e.target.parentElement;
                                                        if (placeholder && !placeholder.querySelector('.placeholder-icon')) {
                                                            placeholder.innerHTML = '<div class="placeholder-icon w-full h-full bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">📷</div>';
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {showImages && (!option[imageKey] || option[valueKey] === '') && (
                                            <div className="w-10 h-10 flex-shrink-0 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                                                {option[valueKey] === '' ? '' : '📷'}
                                            </div>
                                        )}
                                        <span className="flex-1">{option[labelKey]}</span>
                                        {isSelected && (
                                            <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;

